/**
 * GroupResourcesPanel — list + add + delete per-group resource links.
 *
 * A "resource" is a URL pinned to the group (Google Doc, drive folder,
 * external site) with a name and optional description. Anyone in the
 * group can add; the creator (or a super-admin) can delete.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { resourcesApi, type GroupResource, ApiClientError } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  groupId: string;
}

export function GroupResourcesPanel({ groupId }: Props) {
  const { user } = useAuth();
  const [rows, setRows] = useState<GroupResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Add form state
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await resourcesApi.list(groupId);
      setRows(data);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await resourcesApi.create(groupId, {
        title: title.trim(),
        url: url.trim(),
        description: description.trim() || undefined,
      });
      setTitle('');
      setUrl('');
      setDescription('');
      setShowAdd(false);
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setFormError(err.message);
      else setFormError(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(r: GroupResource) {
    if (!window.confirm(`Remove "${r.title}"?`)) return;
    setBusyId(r.id);
    try {
      await resourcesApi.remove(groupId, r.id);
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  function canDelete(r: GroupResource): boolean {
    if (!user) return false;
    return r.createdById === user.id || user.role === 'super_admin' || user.role === 'group_admin';
  }

  function domainOf(u: string): string {
    try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/40">
          {loading ? 'Loading…' : `${rows.length} resource${rows.length === 1 ? '' : 's'}`}
        </p>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="btn-primary inline-flex items-center gap-1.5 text-sm px-3 py-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {showAdd ? 'Cancel' : 'Add resource'}
        </button>
      </div>

      {showAdd && (
        <form className="card space-y-2" onSubmit={handleAdd}>
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-white/60 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={255}
                required
                className="input"
                placeholder="Class reunion sign-up"
              />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">URL</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                maxLength={500}
                required
                className="input"
                placeholder="https://docs.google.com/…"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              className="input"
              placeholder="What is this link for?"
            />
          </div>
          {formError && <p className="text-sm text-red-400">{formError}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving || !title.trim() || !url.trim()} className="btn-primary">
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      {!loading && rows.length === 0 && !showAdd && (
        <div className="card text-center py-8 text-white/60">
          <p className="text-base text-white mb-1">No resources yet</p>
          <p className="text-sm">Pin a link — sign-up sheet, Drive folder, anything useful.</p>
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="card flex items-center justify-between gap-3">
            <div className="min-w-0">
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-medium hover:text-primary truncate block"
              >
                {r.title}
              </a>
              {r.description && (
                <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{r.description}</p>
              )}
              <p className="text-[11px] text-white/40 mt-0.5">
                {domainOf(r.url) || r.url} · added by {r.createdByName ?? '—'} · {new Date(r.createdAt).toLocaleDateString()}
              </p>
            </div>
            {canDelete(r) && (
              <button
                type="button"
                onClick={() => handleDelete(r)}
                disabled={busyId === r.id}
                className="text-xs px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30 flex-shrink-0"
                aria-label={`Remove ${r.title}`}
              >
                {busyId === r.id ? '…' : 'Remove'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default GroupResourcesPanel;
